import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Calendar } from "lucide-react";

const BLANK = {
  title: "", course_id: "", subject_id: "", instructor_name: "",
  description: "", class_type: "live",
  start_time: "", end_time: "", timezone: "IST",
  meeting_link: "", materials: [], student_ids: [], status: "upcoming",
};

export default function SchedulesTab() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = () => Promise.all([
    api.get("/admin/classes"),
    api.get("/admin/users"),
    api.get("/admin/courses"),
    api.get("/subjects"),
  ]).then(([c, u, co, s]) => { setItems(c.data); setUsers(u.data); setCourses(co.data); setSubjects(s.data); });
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...BLANK, ...c,
      course_id: c.course_id || "", subject_id: c.subject_id || "",
      meeting_link: c.meeting_link || "", description: c.description || "",
      student_ids: c.student_ids || [],
      // normalize to datetime-local accepted
      start_time: toLocal(c.start_time), end_time: toLocal(c.end_time),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        course_id: form.course_id || null,
        subject_id: form.subject_id || null,
        meeting_link: form.meeting_link || null,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
      };
      if (editing) await api.put(`/admin/classes/${editing.id}`, payload);
      else await api.post("/admin/classes", payload);
      toast.success(editing ? "Class updated" : "Class scheduled");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const del = async (c) => {
    if (!confirm(`Delete class "${c.title}"?`)) return;
    await api.delete(`/admin/classes/${c.id}`);
    toast.success("Deleted"); load();
  };
  const toggleStudent = (uid) => {
    const set = new Set(form.student_ids);
    if (set.has(uid)) set.delete(uid); else set.add(uid);
    setForm({ ...form, student_ids: Array.from(set) });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="new-class-btn">
          <Plus className="mr-2 h-4 w-4" /> Schedule class
        </Button>
      </div>
      <Card className="border-border/70">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Title</TableHead>
              <TableHead>When</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Instructor</TableHead>
              <TableHead>Students</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.title}</TableCell>
                  <TableCell className="text-xs">{new Date(c.start_time).toLocaleString("en-IN")}</TableCell>
                  <TableCell className="capitalize text-xs">{c.class_type}</TableCell>
                  <TableCell className="text-xs">{c.instructor_name || "—"}</TableCell>
                  <TableCell className="text-xs">{(c.students || []).length}</TableCell>
                  <TableCell className="capitalize"><span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{c.status}</span></TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-class-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(c)} data-testid={`del-class-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="p-10 text-center text-sm text-muted-foreground">No classes scheduled yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit class" : "Schedule a class"}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="class-title" /></div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label>Course (optional)</Label>
                <Select value={form.course_id || "none"} onValueChange={(v) => setForm({ ...form, course_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="class-course"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject (optional)</Label>
                <Select value={form.subject_id || "none"} onValueChange={(v) => setForm({ ...form, subject_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="class-subject"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Instructor</Label><Input value={form.instructor_name} onChange={(e) => setForm({ ...form, instructor_name: e.target.value })} data-testid="class-instructor" /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label>Class type</Label>
                <Select value={form.class_type} onValueChange={(v) => setForm({ ...form, class_type: v })}>
                  <SelectTrigger data-testid="class-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="live">Live online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="demo">Demo</SelectItem>
                    <SelectItem value="revision">Revision</SelectItem>
                    <SelectItem value="recorded">Recorded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Start</Label><Input type="datetime-local" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="class-start" /></div>
              <div><Label>End</Label><Input type="datetime-local" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="class-end" /></div>
            </div>
            <div><Label>Meeting link (Zoom / Meet / Teams URL)</Label><Input value={form.meeting_link || ""} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://..." data-testid="class-link" /></div>
            <div><Label>Description / instructions</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="class-desc" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="class-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="ongoing">Ongoing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="postponed">Postponed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigned students ({form.student_ids.length} selected)</Label>
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-border p-2">
                {users.filter((u) => u.role === "student").map((u) => (
                  <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-secondary">
                    <input type="checkbox" checked={form.student_ids.includes(u.id)} onChange={() => toggleStudent(u.id)} data-testid={`cls-student-${u.id}`} />
                    <span>{u.name} · {u.email}</span>
                  </label>
                ))}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Tip: If you link a course above, every enrolled student in that course will also see this class.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="class-save-btn">
              <Calendar className="mr-2 h-4 w-4" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function toLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
